import React, { useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useSessionStorage } from 'usehooks-ts';
import { general } from '../utils/Gifts/transforms.gifts.settings';
import transformGiftsData from '../utils/Gifts/transforms.gifts.data';
import { productsStorageSaveProducts } from '../store/Slices/gifts/productsStorageSlice';
import rollbar from '../utils/rollbar-agent';
import { isValidGUID } from '../utils/attribution';
import {
  localStorageGetItem,
  localStorageSetItem,
  sessionStorageSetItem,
} from '../utils/web-storage';
import ErrorOverlay from '../components/01_atoms/ErrorOverlay';
import { serializeError } from 'serialize-error';

// Load layout dynamically to avoid loading global styles for CNET app.
const GiftsLayout = dynamic(() => import('./gifts_layout'));

const GiftsApplication = (props) => {
  const {
    giftsSiteSettings = null,
    giftsData = null,
    isDataFromLocalStorage = true,
    entity = null,
    statusCode = 200,
    nonce = '',
  } = props;
  const router = useRouter();
  let sourceCode;
  if (isValidGUID(router.query.source)) {
    sourceCode = router.query.source;
  }

  const [noNavMode, setNoNavMode, removeNoNavMode] = useSessionStorage(
    'no_nav',
    router.query.no_nav,
  );

  useLayoutEffect(() => {
    if (router.query.no_nav === '1') {
      setNoNavMode('1');
    }
  }, []);

  useEffect(() => {
    if (!noNavMode) return;

    // Paths that should immediately reset/disable no-nav mode.
    const resetPathPatterns = [
      /^\/$/, // Gifts home page.
      /^\/checkout\/\d+\/complete$/, // Gifts thank-you page.
    ];

    if (resetPathPatterns.some((re) => re.test(router.asPath))) {
      removeNoNavMode();
      return;
    }

    const onRouteChangeComplete = (nextUrl) => {
      const [pathname = '', qs = ''] = nextUrl.split('?');
      // If the no_nav parameter is present, do not reset the no_nav mode.
      const noNavParam = new URLSearchParams(qs).get('no_nav');
      if (noNavParam === '1') {
        return;
      }

      const isReset = !!pathname && resetPathPatterns.some((re) => re.test(pathname));
      if (isReset) {
        removeNoNavMode();
      }
    };
    router.events.on('routeChangeComplete', onRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [noNavMode]);

  useEffect(() => {
    if (!isDataFromLocalStorage) {
      localStorageSetItem('giftsSiteSettings', JSON.stringify(giftsSiteSettings));
    }
    if (!isDataFromLocalStorage) {
      localStorageSetItem('giftsData', JSON.stringify(giftsData));
    }
    // Store attribution data if source code is presented in URL.
    if (sourceCode) {
      sessionStorageSetItem('cw_gifts_attribution', sourceCode);
    }
  }, []);

  if (statusCode === 500) {
    return (
      <ErrorOverlay statusCode={500} app="gifts">
        Internal error
      </ErrorOverlay>
    );
  }

  return (
    <GiftsLayout
      {...props}
      nonce={nonce}
      entity={entity}
      statusCode={statusCode}
      siteContentSettings={giftsSiteSettings}
      giftsData={giftsData}
    />
  );
};

GiftsApplication.getInitialProps = async ({ store, ctx: { res, req, query, asPath } }) => {
  const initialProps = {
    query,
    originalUrl: req ? req.originalUrl : asPath,
    isDataFromLocalStorage: true,
  };

  if (res?.settings) {
    try {
      initialProps.giftsSiteSettings = await general(res.settings);

      // The condition was added just for testing purposes.
      // It used by our test suite to test the Single Gifts campaign case.
      if (
        query.testGiftsSingleCampaign &&
        initialProps.giftsSiteSettings?.checkoutPage?.sourceCodes.length > 1
      ) {
        initialProps.giftsSiteSettings.checkoutPage.sourceCodes =
          initialProps.giftsSiteSettings.checkoutPage.sourceCodes.slice(0, 1);
      }
    } catch (error) {
      rollbar.error('Could not get gifts site settings.', {
        error: serializeError(error),
        originalUrl: initialProps.originalUrl,
      });
    }

    if (
      initialProps.giftsSiteSettings.frontPage.homepageLink.url ===
      initialProps.originalUrl.split('?')[0]
    ) {
      res.redirect(
        301,
        `/${query.previewToken && query.previewMode ? `?previewMode=1&previewToken=${query.previewToken}` : ''}`,
      );
    }

    // Ignore redirects in Preview mode.
    if (initialProps.giftsSiteSettings.redirects.length && !query.previewToken) {
      // Handle redirects.
      const redirect = initialProps.giftsSiteSettings.redirects.find(
        (item) => item.fromUrl === initialProps.originalUrl.split('?')[0],
      );

      if (redirect) {
        res.redirect(+redirect.statusCode, redirect.toUrl);
      }
    }
  }

  if (res?.cwGiftsData) {
    try {
      const products = await transformGiftsData.products(
        res.cwGiftsData.products,
        initialProps.giftsSiteSettings,
      );
      initialProps.giftsData = {
        categories: await transformGiftsData.categories(
          res.cwGiftsData.categories,
          initialProps.giftsSiteSettings,
        ),
        pages: await transformGiftsData.pages(
          res.cwGiftsData.pages,
          initialProps.giftsSiteSettings,
        ),
      };
      initialProps.isDataFromLocalStorage = false;

      store.dispatch(productsStorageSaveProducts(products));
    } catch (error) {
      // In case of any error during transform, we shouldn't save it to local storage.
      initialProps.isDataFromLocalStorage = true;
      rollbar.error('Could not transform gifts data.', {
        error: serializeError(error),
        originalUrl: initialProps.originalUrl,
      });
    }
  }

  if (!res) {
    initialProps.giftsSiteSettings = JSON.parse(localStorageGetItem('giftsSiteSettings'));
    initialProps.giftsData = JSON.parse(localStorageGetItem('giftsData'));
  }

  if (!initialProps.giftsSiteSettings || !initialProps.giftsData) {
    initialProps.statusCode = 500;
    return initialProps;
  }

  // Determine the page entity for categories.
  if (initialProps.originalUrl.startsWith('/category/')) {
    const categories = initialProps.giftsData.categories;
    const results = Object.values(categories).filter(
      (item) => item.url.url === initialProps.originalUrl.split('?')[0],
    );

    if (!results.length) {
      initialProps.statusCode = 404;
    } else {
      initialProps.entity = results[0];
    }
  }
  // Determine the page entity for products.
  else if (initialProps.originalUrl.startsWith('/gifts/')) {
    const products = store.getState().productsStorage;
    const results = Object.values(products.gifts).filter(
      (item) => item.url.url === initialProps.originalUrl.split('?')[0],
    );

    if (!results.length) {
      initialProps.statusCode = 404;
    } else {
      initialProps.entity = results[0];
    }
  }
  // Determine the page entity for corporate products.
  else if (initialProps.originalUrl.startsWith('/corporate/')) {
    const products = store.getState().productsStorage;
    const results = Object.values(products.corporateGifts).filter(
      (item) => item.url.url === initialProps.originalUrl.split('?')[0],
    );

    if (!results.length) {
      initialProps.statusCode = 404;
    } else {
      initialProps.entity = results[0];
    }
  }
  // Determine the home page and gift cards.
  else if (
    initialProps.originalUrl === '/' ||
    initialProps.originalUrl.startsWith('/?') ||
    initialProps.originalUrl.startsWith('/gift-card/')
  ) {
    const pages = initialProps.giftsData.pages;

    const results = Object.values(pages).filter(
      (item) => item.url.url === initialProps.giftsSiteSettings.frontPage.homepageLink.url,
    );

    if (!results.length) {
      initialProps.statusCode = 404;
    } else {
      initialProps.entity = results[0];
    }
  }
  // Determine the basket, cards, checkout pages.
  else if (
    ['/basket', '/cards', '/checkout'].some(
      (path) =>
        initialProps.originalUrl === path || initialProps.originalUrl.startsWith(path + '?'),
    )
  ) {
    // Ok, page is detected, no actions.
  }
  // Determine the checkout complete page.
  else if (/\/checkout\/\d+\/complete/.test(initialProps.originalUrl)) {
    // Ok, page is detected, no actions.
  }
  // Determine the page entity for other pages.
  else {
    // Other pages.
    const pages = initialProps.giftsData.pages;

    const results = Object.values(pages).filter(
      (item) => item.url.url === initialProps.originalUrl.split('?')[0],
    );

    if (!results.length) {
      initialProps.statusCode = 404;
    } else {
      initialProps.entity = results[0];
    }
  }

  // Set 404 status code.
  if (res && initialProps.statusCode === 404) {
    res.statusCode = 404;
  }

  return initialProps;
};

GiftsApplication.propTypes = {
  giftsSiteSettings: PropTypes.object,
  giftsData: PropTypes.object,
  isDataFromLocalStorage: PropTypes.bool,
  entity: PropTypes.object,
  statusCode: PropTypes.number,
  nonce: PropTypes.string,
};

export default GiftsApplication;
